import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CForm,
    CFormInput,
    CFormSelect,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
    CTableDataCell,
} from '@coreui/react';

import { apiClient } from 'services/Api';
import ToastNotification from 'components/globals/ToastNotification';

const DepartmentAllocationsPage = () => {
    const currentYear = new Date().getFullYear();

    const [toast, setToast] = useState(null);
    const [selectedYear, setSelectedYear] = useState(currentYear);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);

    const [rows, setRows] = useState([]); // {id, year, user, user_name, department, department_name, weight}
    const availableYears = (startYear = 2019) => {
        const currentYear = new Date().getFullYear();
        const start = Number.isFinite(startYear) ? startYear : 2019;
        const len = Math.max(1, currentYear - start + 1);
        return Array.from({ length: len }, (_, i) => start + i);
    };

    const fetchRows = useCallback(async (year) => {
        setLoading(true);
        try {
            const res = await apiClient.get(`/department-allocations/?year=${year}`);
            const data = res.data || [];
            setRows(data);
        } catch (err) {
            setRows([]);
            setToast({
                title: 'Alocação por Departamento',
                message: 'Erro ao carregar alocações.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRows(selectedYear);
    }, [selectedYear, fetchRows]);

    const totalsByUser = useMemo(() => {
        const map = {};
        rows.forEach((r) => {
            map[r.user] = (map[r.user] || 0) + (Number(r.weight) || 0);
        });
        return map;
    }, [rows]);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            await apiClient.post('/department-allocations/generate/', { year: selectedYear });
            await fetchRows(selectedYear);
            setToast({
                title: 'Alocação por Departamento',
                message: 'Registos gerados com sucesso (alocação=0 para novos).',
                type: 'success',
            });
        } catch (err) {
            setToast({
                title: 'Alocação por Departamento',
                message: 'Erro ao gerar registos.',
                type: 'error',
            });
        } finally {
            setGenerating(false);
        }
    };

    const handleSaveAll = async () => {
        // validação: nenhum utilizador > 100
        const offenders = Object.entries(totalsByUser).filter(([, total]) => total > 100);
        if (offenders.length) {
            setToast({
                title: 'Alocação por Departamento',
                message: 'Existem utilizadores com soma de pesos superior a 100%.',
                type: 'error',
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                year: selectedYear,
                items: rows.map((r) => ({
                    user: r.user,
                    department: r.department,
                    weight: Number(r.weight) || 0,
                })),
            };
            await apiClient.post('/department-allocations/bulk/', payload);

            setToast({
                title: 'Alocação por Departamento',
                message: 'Alocações guardados com sucesso.',
                type: 'success',
            });

            await fetchRows(selectedYear);
        } catch (err) {
            setToast({
                title: 'Alocação por Departamento',
                message: 'Erro ao guardar alocações.',
                type: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const getRowKey = (r) => r.id ?? `${r.year}-${r.user}-${r.department}`;


    const onChangeWeight = (rowKey) => (e) => {
        const raw = e.target.value;
        const num = raw === '' ? 0 : Number(raw);
        if (Number.isNaN(num) || num < 0 || num > 100) return;

        setRows((prev) =>
            prev.map((r) => (getRowKey(r) === rowKey ? { ...r, weight: num } : r))
        );
    };

    const groupedByUser = useMemo(() => {
        const map = new Map(); // userId -> rows[]
        rows.forEach((r) => {
            const key = r.user;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        });
        return Array.from(map.entries()); // [[userId, rows[]], ...]
    }, [rows]);


    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard className="mb-4">
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="mb-0">Alocação Utilizador/Departamento</h4>
                        <small className="text-muted">A soma por utilizador no ano não pode exceder 100%.</small>
                    </div>

                    <CRow className="g-2 align-items-center">
                        <CCol xs="auto">
                            <strong>Ano:&nbsp;</strong>
                        </CCol>
                        <CCol xs="auto">
                            <CFormSelect
                                size="sm"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                            >
                                {availableYears().map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CCol>

                        <CCol xs="auto">
                            <CButton color="info" className="text-white" onClick={handleGenerate} disabled={generating}>
                                {generating ? <CSpinner size="sm" /> : 'Gerar registos do ano'}
                            </CButton>
                        </CCol>

                        <CCol xs="auto">
                            <CButton color="primary" onClick={handleSaveAll} disabled={saving || loading}>
                                {saving ? <CSpinner size="sm" /> : 'Guardar tudo'}
                            </CButton>
                        </CCol>
                    </CRow>
                </CCardHeader>

                <CCardBody>
                    {loading ? (
                        <div className="text-center my-3">
                            <CSpinner size="sm" /> A carregar…
                        </div>
                    ) : rows.length === 0 ? (
                        <CAlert color="info">
                            Não existem registos para este ano. Clica em <strong>Gerar registos do ano</strong>.
                        </CAlert>
                    ) : (
                        <CForm
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSaveAll();
                            }}
                        >
                            <CTable bordered small responsive align="middle">
                                <CTableHead>
                                    <CTableRow>
                                        <CTableHeaderCell>Utilizador</CTableHeaderCell>
                                        <CTableHeaderCell>Departamento</CTableHeaderCell>
                                        <CTableHeaderCell className="text-center" style={{ width: 160 }}>
                                            Alocação (%)
                                        </CTableHeaderCell>
                                        <CTableHeaderCell className="text-center" style={{ width: 160 }}>
                                            Total utilizador (%)
                                        </CTableHeaderCell>
                                    </CTableRow>
                                </CTableHead>

                                <CTableBody>
                                    {groupedByUser.map(([userId, userRows], groupIndex) => {
                                        const total = totalsByUser[userId] || 0;
                                        const over = total > 100;
                                        const rowSpan = userRows.length;
                                        const isStriped = groupIndex % 2 === 1;

                                        return userRows.map((r, idx) => {
                                            const rowKey = getRowKey(r);

                                            return (
                                                <CTableRow key={rowKey} className={isStriped ? 'table-striped-group' : ''}>
                                                    {idx === 0 && (
                                                        <CTableDataCell rowSpan={rowSpan} style={{ verticalAlign: 'middle' }}>
                                                            {r.user_name || '—'}
                                                        </CTableDataCell>
                                                    )}

                                                    <CTableDataCell>{r.department_name || '—'}</CTableDataCell>

                                                    <CTableDataCell className="text-center">
                                                        <CFormInput
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            step={1}
                                                            value={Number(r.weight) || 0}
                                                            onChange={onChangeWeight(rowKey)}
                                                        />
                                                    </CTableDataCell>

                                                    {idx === 0 && (
                                                        <CTableDataCell
                                                            rowSpan={rowSpan}
                                                            className={`text-center ${over ? 'text-danger fw-semibold' : ''}`}
                                                            style={{ verticalAlign: 'middle' }}
                                                        >
                                                            {total}%
                                                        </CTableDataCell>
                                                    )}
                                                </CTableRow>
                                            );
                                        });
                                    })}
                                </CTableBody>

                            </CTable>
                        </CForm>
                    )}
                </CCardBody>
            </CCard>
        </>
    );
};

export default DepartmentAllocationsPage;
